import { Box, Button, Divider, Grid, Paper, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { useNavigate } from 'react-router-dom';
import { RoutesEnum } from 'src/shared/models/enums/routes.enum';
import { FundraiserTypeEnum, getFundraiserTypeText } from 'src/modules/gift/models/fundraiserType.enum';
import { NumberFormatter } from 'src/shared/components/atoms/numberFormatter';
import { FundraiserSummaryChartPie } from './fundraiserSummaryChartPie';
import { FundraiserResponse } from '../models/fundraiserResponse';

type FundraiserTotalsSummaryProps = {
  fundraiser?: FundraiserResponse;
};

export const FundraiserTotalsSummary = (props: FundraiserTotalsSummaryProps) => {
  const { fundraiser } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();

  const redirect = (id: number) => {
    navigate(`${RoutesEnum.FundraiserSettingsDetail}/${id}`);
  };

  return (
    <>
      <Paper sx={{ height: '100%', p: 2 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={4} md={4} lg={4} sx={{ mb: { xs: 2, sm: 0 } }}>
            <Typography sx={{ fontWeight: 600, mb: 2 }}>{t('fundraiser:totalRaised')}</Typography>
            <FundraiserSummaryChartPie paymentDistribution={fundraiser?.paymentDistribution} totalAmount={fundraiser?.totalAmount || 0} />
          </Grid>

          <Grid item xs={12} sm={7} md={7} lg={7} sx={{ mt: 1, display: 'flex' }}>
            <Grid container direction='column' sx={{ flex: 1 }}>
              {/* TOPP-rad */}
              <Grid item sx={{ mb: { xs: 3, md: 0 }, mr: { xs: 4, sm: 0 } }}>
                <Grid container justifyContent='space-between'>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('fundraiser:donations')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant='h5' fontWeight={(thm) => thm.typography.fontWeightBold}>
                        {fundraiser?.totalDonations || 0}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {t('fundraiser:count')}
                      </Typography>
                    </Box>
                  </Grid>

                  <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />

                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('fundraiser:donationsPerDay')}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                      <Typography variant='h5' fontWeight={(thm) => thm.typography.fontWeightBold}>
                        <NumberFormatter number={fundraiser?.averageDonations ?? 0} decimals={1} />
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        {t('fundraiser:count')}
                      </Typography>
                    </Box>
                  </Grid>

                  <Divider orientation='vertical' flexItem sx={{ mx: 2 }} />

                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600, mb: 1 }}>{t('fundraiser:averageDonationAmount')}</Typography>
                    <Typography variant='h5' fontWeight={(thm) => thm.typography.fontWeightBold}>
                      <AmountWithCurrency amount={fundraiser?.averageAmount || 0} decimals={0} showCurrencyInSecondaryStyle />
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>

              {/* BOTTOM-rad */}
              <Grid item sx={{ mt: 'auto', mr: { xs: 4, sm: 0 } }}>
                <Grid container justifyContent='space-between'>
                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:name')}</Typography>
                    <Typography>{fundraiser?.heading || ''}</Typography>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:created')}</Typography>
                    <Typography>{DateHelper.getDate(fundraiser?.createdDate) || ''}</Typography>
                  </Grid>

                  <Grid item xs={3}>
                    <Typography sx={{ fontWeight: 600 }}>{t('fundraiser:typeOfFund')}</Typography>
                    <Typography>{getFundraiserTypeText(fundraiser?.type || FundraiserTypeEnum.unknown, t) || ''}</Typography>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={12} mb={-1} display='flex' justifyContent='flex-end'>
            <Button variant='text' disabled={!fundraiser} size='small' onClick={() => redirect(fundraiser?.fundraiser_id || 0)}>
              {t('common:button.settings')}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};
